<template>
  <FormGroup>
    <View direction="column" gap="sm">
      <Text variant="h1">Check your email</Text>
      <Text variant="body-large" color="secondary">We've sent you a six-digit confirmation code to</Text>
      <Text variant="label-large">{{ route.query.email }}</Text>
    </View>

    <FormField label="Enter 6-digit code" :error="form.fieldErrors('verificationCode')">
      <View align="center" gap="md">
        <CodeInput v-model="form.fields.verificationCode" :disabled="form.submitting" @complete="verify" />
        <LoadingIndicator v-if="form.submitting" />
      </View>
    </FormField>

    <div v-if="!isCodeResent">
      <span>Don't receive the code?&nbsp;</span>
      <Link color="accent" @click="resendEmailConfirmationCode">Resend code</Link>
    </div>

    <div v-else>The code has been resent.</div>
  </FormGroup>
</template>

<script setup lang="ts">
import { maxLength, minLength, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref, watch } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import {
  CodeInput,
  extractErrorMessage,
  FormField,
  FormGroup,
  handleError,
  Link,
  LoadingIndicator,
  Text,
  View,
  useForm,
} from '@tallo/design-system';

import { useAuthStore } from '../auth.store';

const route = useRoute();
const authStore = useAuthStore();
const router = useRouter();

const props = defineProps<{
  /**
   * Custom function to be executed when the verification code is submitted
   * @param code - The verification code to be verified
   * @description if not provided, the component will use the default "one time code" sign in verification function
   */
  customVerifyCodeRequest?: (code: number) => Promise<void>;
  /**
   * Custom function to be executed when the "Resend code" link is clicked
   * @description if not provided, the component will use the default "one time code" sign in resend code function
   */
  customResendCodeRequest?: () => Promise<void>;
  navigationAfterVerification?: RouteLocationRaw;
}>();

const renterEmail = route.query.email as string;
const invalidCodeMessage = 'This code is invalid or has expired. Check it or resend the code.';
const isCodeResent = ref(false);

const form = useForm(
  {
    verificationCode: '',
  },
  {
    verificationCode: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(6),
    },
  },
);

watch(
  () => form.fields.verificationCode,
  () => form.clearValidationErrors(),
);

function verify() {
  form.submit(async () => {
    const code = Number(form.fields.verificationCode);

    try {
      if (props.customVerifyCodeRequest) {
        await props.customVerifyCodeRequest(code);
      } else {
        await authStore.signInWithOneTimeCode(renterEmail as string, code);

        // Resume the link the guard bounced to sign-in from, if any; a 2FA-gated account is
        // redirected to the code screen by the router guard instead.
        const redirect = authStore.consumePostSignInRedirect() ?? props.navigationAfterVerification;

        if (redirect) {
          await router.push(redirect);
        }
      }
    } catch (error) {
      form.setFieldError(
        'verificationCode',
        props.customVerifyCodeRequest ? extractErrorMessage(error, invalidCodeMessage) : signInCodeErrorMessage(error),
      );
    }
  });
}

// A wrong code and an email with no account read the same, so the form never says which accounts exist
function signInCodeErrorMessage(error: unknown): string {
  const status = (error as AxiosError)?.response?.status;

  return status === 400 || status === 401 ? invalidCodeMessage : extractErrorMessage(error);
}

async function resendEmailConfirmationCode() {
  try {
    if (props.customResendCodeRequest) {
      await props.customResendCodeRequest();
    } else {
      await authStore.requestOneTimeSignInCode(renterEmail);
    }
  } catch (error) {
    handleError(error);
    return;
  }

  isCodeResent.value = true;

  setTimeout(() => (isCodeResent.value = false), 30_000);
}
</script>
